import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { PreloadedQuery, useFragment, usePreloadedQuery } from "react-relay";

import { useGetValidCourseAndDurationSelections } from "@/features/BookingParts/hooks/useGetValidCourseAndDurationSelections/useGetValidCourseAndDurationSelections";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { getBookingGroupSizeAtom } from "../../../../atoms/selectors/getBookingGroupSizeAtom/getBookingGroupSizeAtom";
import { currentDurationOptionsAtom } from "../../../../atoms/stores";
import { BookingDurationCourtInnerFragment$key } from "./__generated__/BookingDurationCourtInnerFragment.graphql";
import { BookingDurationCourtInnerQuery } from "./__generated__/BookingDurationCourtInnerQuery.graphql";
import { bookingCourtAtom } from "./atoms/stores";
import { BookingDurationControl } from "./components/BookingDurationControl";
import { CourtsControl } from "./components/CourtsControl";

export type CourseDurationOption = {
  maxDurationMins: number;
  startSlot: {
    id: string;
    time: string;
    course: {
      id: string;
      courseBucket: {
        id: string;
      };
      name: string;
      description?: string;
    };
  };
};

type BookingDurationCourtInnerProps = {
  queryRef: PreloadedQuery<BookingDurationCourtInnerQuery>;
  slotRef: BookingDurationCourtInnerFragment$key | null;
  durationMins: number;
  isLessonBooking: boolean;
};

export const BookingDurationCourtInner = ({
  queryRef,
  slotRef,
  durationMins,
  isLessonBooking,
}: BookingDurationCourtInnerProps) => {
  const data = usePreloadedQuery(
    BookingDurationCourtInnerQueryString,
    queryRef
  );
  const slot = useFragment(BookingDurationCourtInnerFragmentString, slotRef);
  const setCurrentDurationOptions = useSetAtom(currentDurationOptionsAtom);
  const groupSize = useAtomValue(getBookingGroupSizeAtom);
  const { validDurationOptions, allValidDurationsOptionsMappedByCourseId } =
    useGetValidCourseAndDurationSelections(
      data?.slotNode ?? null,
      groupSize ?? 0
    ) ?? [];

  setCurrentDurationOptions(allValidDurationsOptionsMappedByCourseId);
  // on which is current court we are on
  const currentCourtInformation = useAtomValue(bookingCourtAtom);
  const currentCourtId = currentCourtInformation?.id ?? slot?.course?.id;
  const canSelectCourt =
    slot?.agenda?.facility?.publicBookingPermissionSet?.customerCanSelectCourt;

  // check if the current court is in a course bucket
  const isInCourseBucket = data?.node?.courseBucket?.id;

  return (
    <VStack>
      {!isLessonBooking ? (
        <BookingDurationControl
          durationOptions={validDurationOptions}
          courseBucketRef={data?.node?.courseBucket ?? null}
          durationMins={durationMins}
        />
      ) : null}
      {isInCourseBucket && canSelectCourt && slot && data?.node ? (
        <CourtsControl
          slotRef={data.slotNode ?? undefined}
          courseBucketRef={data?.node?.courseBucket ?? null}
          currentCourtId={currentCourtId ?? null}
        />
      ) : null}
    </VStack>
  );
};
export const BookingDurationCourtInnerQueryString = graphql`
  query BookingDurationCourtInnerQuery(
    $courseId: ID!
    $startTime: Time!
    $agendaId: ID!
    $slotId: ID!
    $bookingType: BookingType!
    $memberId: ID!
    $bookingId: ID!
  ) {
    slotNode: node(id: $slotId) {
      ...useGetValidCourseAndDurationSelections_Node
      ...CourtsControlFragment
    }
    node(id: $courseId) {
      ... on Course {
        courseBucket {
          id
          ...useGetCourseOptions_CourseBucket
        }
      }
    }
  }
`;

export const BookingDurationCourtInnerFragmentString = graphql`
  fragment BookingDurationCourtInnerFragment on Slot {
    id
    ... on TimeSlot {
      id
      time
      course {
        id
        name
        description
        courseBucket {
          id
        }
      }
    }
    agenda {
      id
      facility {
        publicBookingPermissionSet {
          customerCanSelectCourt
        }
      }
    }
  }
`;
